// The hat, on the League page (PLAN-2026-10-30-the-weekly-hat §7): who is
// wearing this week's hat, sulking in it on its own stage, who had the most
// points and picked it, and, to that team's manager, every hat to pick from,
// a tap putting one on. It can be changed until the next week's is decided.
//
// THE CHARACTERS WEAR IT FROM THE LEAGUE'S HAT (`LeagueHatScope`), as they do
// everywhere else, so the panel draws no hat of its own on them.

import { useState } from 'react';
import {
  WEEKLY_HATS,
  weeklyHatName,
  weeklyHatSaid,
  type LeagueHat,
  type TeamRef,
} from '@fsp/domain';
import { usePickHat } from '../../data/hat';
import { HatArt } from '../../kit/FaceArt';
import { TeamFigure } from '../../kit/TeamFigure';
import { Text, tell } from '../../kit/ui';

/** Names in a sentence: *A*, *A and B*, *A, B and C*. */
function named(names: readonly string[]): string {
  return names.length <= 1
    ? (names[0] ?? 'Somebody')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

export function HatPanel({
  hat,
  teams,
  myTeamId,
  label,
}: {
  hat: LeagueHat;
  /** Every team in the league, to name and draw the wearers and pickers. */
  teams: readonly TeamRef[];
  myTeamId: string | null;
  /** The week it is for, as the league names it: *Week 7*. */
  label: string;
}) {
  const pick = usePickHat();
  const [busy, setBusy] = useState(false);
  const wearers = hat.wearers.flatMap((id) => teams.find((t) => t.id === id) ?? []);
  const pickers = hat.pickers.flatMap((id) => teams.find((t) => t.id === id) ?? []);
  const mine = myTeamId !== null && hat.pickers.includes(myTeamId);
  const wearing = named(wearers.map((t) => t.name));
  const pickerName = teams.find((t) => t.id === hat.pickedBy)?.name ?? 'the high scorer';
  // THE DRAWN HAT CAN BE KEPT: picking it is a pick, which says so.
  const choose = async (h: (typeof WEEKLY_HATS)[number]) => {
    if (busy || (h === hat.hat && hat.pickedBy !== null)) return;
    setBusy(true);
    const problem = await pick(h);
    setBusy(false);
    if (problem !== null) tell(problem, 'bad');
    else tell(`On it goes: ${weeklyHatSaid(h)}.`, 'good');
  };
  return (
    <div className="fsp-hat">
      <div className="fsp-hat-stage">
        {wearers.map((t) => (
          <TeamFigure key={t.id} team={t} mood="annoyed" height={132} stage />
        ))}
      </div>
      <div className="fsp-hat-words">
        <Text block>
          {`${wearing} ${wearers.length > 1 ? 'are' : 'is'} wearing ${weeklyHatSaid(hat.hat)} for ${label}’s fewest points.`}
        </Text>
        <Text tone="secondary" block>
          {mine
            ? hat.pickedBy === null
              ? `You had the most points, so you pick the hat for ${wearing}. The draw put this one on for now.`
              : hat.pickedBy === myTeamId
                ? 'You had the most points and picked it. You can change it until next week’s hat goes on.'
                : `You tied for the most points, and ${pickerName} picked it. You can change it until next week’s hat goes on.`
            : hat.pickedBy === null
              ? `${named(pickers.map((t) => t.name))} had the most points and ${pickers.length > 1 ? 'pick' : 'picks'} the hat. The draw put this one on for now.`
              : `${named(pickers.map((t) => t.name))} had the most points, and ${pickerName} picked it.`}
        </Text>
      </div>
      {mine ? (
        <div className="fsp-hat-pick" role="group" aria-label={`Pick the hat for ${wearing}`}>
          {WEEKLY_HATS.map((h) => (
            <button
              key={h}
              type="button"
              className="fsp-hat-choice"
              aria-pressed={h === hat.hat}
              disabled={busy}
              onClick={() => void choose(h)}
            >
              <HatArt hat={h} width={52} />
              <span className="fsp-hat-name">{weeklyHatName(h)}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
